import React, { type FC, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Link, Space, Text } from '@g2u/ui-kit';
import { Divider, Form, Spin } from 'antd';
import { useQuery } from 'react-query';

import env from 'env';

import {
  AccountNotesFields,
  AccountTypeFields,
  BillingAddressFields,
  CustomerExtraInfo,
  HearAboutUsFields,
  HomeAddressFields,
  PrimaryContactFields,
  SecondaryContactFields,
} from 'components';
import { Toast, parseErrors, useCustomerProducts, useDiscountOptions, useDiscountData } from 'helpers';
import { encryptAesToken } from 'helpers/crypto';
import { AccountType, PrepaidProductStatus, type JsonDataItem } from 'types';
import { getHearAboutUs } from 'queries';

export namespace CustomerDetails {
  export type Props = Readonly<{
    customer: JsonDataItem | null;
    onUpdate: (payload: JsonDataItem) => void;
    isUpdating?: boolean;
    isUpdated?: boolean;
    isLocked: boolean;
  }>;

  export const $: FC<Props> = (props) => {
    const { customer, onUpdate, isUpdating, isUpdated, isLocked } = props;
    const [form] = Form.useForm();

    const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
    const [accountType, setAccountType] = useState<AccountType>();
    const { prepaidProducts } = useCustomerProducts(customer?.ID, PrepaidProductStatus.New);

    const { data: marketingOptions } = useQuery(['marketing-options'], getHearAboutUs, {
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    const { systemDiscounts } = useDiscountData(customer?.ID);

    const { getLabel } = useDiscountOptions(systemDiscounts);

    const customerDiscounts = systemDiscounts?.filter((discount: JsonDataItem) => discount.type === 'Customer');

    const handleUpdateCustomerFile = (formData: JsonDataItem) => {
      const payload = { id: customer?.ID, ...formData };
      onUpdate(payload);
    };

    useEffect(() => {
      isUpdated && setIsFormChanged(false);
    }, [isUpdated]);

    useEffect(() => {
      setAccountType(customer?.type === 'Personal' ? AccountType.Personal : AccountType.Company);
    }, [customer?.type]);

    return (
      <>
        <Helmet>
          <title>Update Customer Details</title>
        </Helmet>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
          <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical}>
            {!customer ? (
              <Space.$ align={Space.Align.Center} direction={Space.Direction.Vertical}>
                <Spin />
              </Space.$>
            ) : (
              <Form
                form={form}
                name="confirm-form"
                layout="horizontal"
                onValuesChange={(changedFields) => {
                  setIsFormChanged(true);
                  changedFields.accountType && setAccountType(changedFields.accountType);
                }}
                onFieldsChange={() => {
                  setIsFormChanged(true);
                }}
                onChange={() => {
                  setIsFormChanged(true);
                }}
                onFinish={handleUpdateCustomerFile}
                onFinishFailed={(data) => {
                  Toast.show({
                    description: data.errorFields.flatMap((field) => field.errors).join(', '),
                    type: Toast.Type.Error,
                    duration: 0,
                  });
                }}
                colon={false}
                preserve={false}
                disabled={isLocked}
              >
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
                  <Link.$
                    href={`${env.LEGACY_BMS_URL}customers/index/edit/id/${encryptAesToken(customer.ID.toString())}`}
                    target="_blank"
                  >
                    <Text.$ type="danger">OPEN THIS FILE IN THE CORE BMS</Text.$>
                  </Link.$>
                  <AccountTypeFields.$ inline customer={customer} />
                </Space.$>

                {!!customerDiscounts?.length && (
                  <CustomerExtraInfo.$
                    type={CustomerExtraInfo.Type.Discount}
                    items={customerDiscounts.map((discount: JsonDataItem) => ({
                      label: `${getLabel(discount.discount_type, discount.amount)} ${discount.discount_desc}`,
                    }))}
                  />
                )}

                {prepaidProducts?.length > 0 ? (
                  <CustomerExtraInfo.$
                    type={CustomerExtraInfo.Type.Prepaid}
                    items={prepaidProducts.map((product: JsonDataItem) => ({
                      label: product.product_name,
                      tooltip: 'The customer has purchased Geeks2U services from Officeworks',
                    }))}
                  />
                ) : (
                  <Divider />
                )}

                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
                  <PrimaryContactFields.$
                    inline
                    form={form}
                    customer={customer}
                    isBusinessAccount={accountType === AccountType.Company}
                  />
                  <SecondaryContactFields.$ inline customer={customer} />
                </Space.$>
                <Divider />
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
                  <HomeAddressFields.$ inline form={form} customer={customer} location={null} />
                  <BillingAddressFields.$ inline form={form} customer={customer} />
                </Space.$>
                <Divider />
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
                  <AccountNotesFields.$ inline customer={customer} hasSpecialNote />
                </Space.$>
                <Divider />
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
                  <HearAboutUsFields.$ customer={customer} inline options={marketingOptions?.data} readonly />
                </Space.$>
              </Form>
            )}
            <Space.$ justify={Space.Justify.End}>
              <Button.$
                type={Button.Type.Primary}
                dataTestId="page-next-btn"
                onClick={() => {
                  form.submit();
                }}
                loading={isUpdating}
                disabled={!isFormChanged || isLocked}
              >
                Save
              </Button.$>
            </Space.$>
          </Space.$>
        </Space.$>
      </>
    );
  };
}
